import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { Divider } from '@mui/material';

import useFormik from 'hooks/useFormik';
import { driverChecklistDefault } from 'common/document/ui/driver-document';
import { IDocumentDetailField } from 'common/document/interfaces/document-detail-field';
import { IDocument } from 'common/document/interfaces/document';
import { IRejectReasonDocument } from 'common/request/interfaces/reject-reson-document';
import { LogEvents } from 'components/Logs/events';
import { DocumentReviewHeader } from '../DocumentViewHeader';
import { validationSchema } from './validationSchema';
import { DocumentStatusEnum } from 'common/document/enums/document-status.enum';
import { IUpdateDocumentImage } from '../DocumentReviewContainer';
import { FileDTO } from 'store/candidate/interfaces';

import styles from './styles.module.scss';
import { OriginalFileParams } from '../DocumentReviewContainer/types';
import { defaultOriginalFileParams } from '../DocumentReviewContainer/constants';
import { DriverChecklistCardForm } from '../DriverChecklistCardFrom';
import ButtonWithArrowIcon from '../../../../components/Buttons/ButtonWithArrowIcon';
import { DocumentRejectionReasonEnum } from '../../../../common/document/enums/document-rejection-reason.enum';
import OutlinedButton from '../../../../components/Buttons/OutlinedButton';
import { AlertColor } from '@mui/material/Alert/Alert';
import { useManager } from '../../../../contexts/access/ManagerContext/useManager';

type InputProps = {
  document: IDocument;
  onReject?: (reason: IRejectReasonDocument) => void;
  onApprove?: (data: { [key: string]: any }) => void;
  onUpdate?: (data: { [key: string]: any }) => void;
  onDownload?: () => void;
  onUpdateDocumentImage?: (data: IUpdateDocumentImage) => void;
  onPurge?: (document: IDocument) => void;
  titlePrev: string;
  titleNext: string;
  onPrev: () => void;
  onNext: () => void;
  isLoadingVerificationStatus?: boolean;
  activeTabDocumentFile: string;
  setActiveTabDocumentFile: (v: string) => void;
  setOriginalFileParams: (v: OriginalFileParams) => void;
  currentOriginalFile?: FileDTO;
  isLoadingOriginalFile: boolean;
  candidateFullName: string;
  employeeId?: string;
};

export const DriverChecklistCardView = ({
  onReject,
  onApprove,
  document,
  onDownload,
  titlePrev,
  titleNext,
  onPrev,
  onNext,
  setOriginalFileParams,
  candidateFullName,
  employeeId,
}: InputProps) => {
  const { isReadOnly } = useManager();

  const handleCloseEditMode = () => {
    setOriginalFileParams(defaultOriginalFileParams);
  };

  useEffect(() => {
    handleCloseEditMode();
  }, [document]);

  const locked = useMemo(() => {
    return ![
      DocumentStatusEnum.UPLOADED,
      DocumentStatusEnum.NEEDS_REVIEW,
    ].includes(document.status);
  }, [document.status]);

  const initialValues = useMemo(() => {
    if (!document?.details || !Object.keys(document.details).length) {
      return {
        ...driverChecklistDefault,
        driverName: candidateFullName,
        alphaCode: employeeId || '',
      };
    }

    return document.details.reduce(
      (hash: { [key: string]: any }, currentValue: IDocumentDetailField) => {
        hash[currentValue.fieldName] = currentValue.fieldValue;
        return hash;
      },
      {}
    );
  }, [document]);

  const { getFieldProps, getFieldErrorProp, handleSubmit } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit(value: { [key: string]: any }) {
      onApprove &&
        onApprove({
          ...value,
        });
    },
  });

  const handleApprove = () => {
    handleSubmit();
  };

  const handleReject = () => {
    onReject &&
      onReject({
        rejectionReason: DocumentRejectionReasonEnum.OTHER,
        rejectionDetails: '',
      });
  };

  const notifications = useMemo(() => {
    if (document.details && Object.keys(document.details).length) {
      const sortMap = new Map([
        ['drugOrAlcohol', 1],
        ['amphetamines', 2],
        ['opiates', 3],
        ['marijuana', 4],
        ['cocaine', 5],
        ['pcp', 6],
        ['otherDrugs', 7],
        ['hearing', 8],
        ['skillPerformanceEvaluation', 9],
        ['alternativeVisionStandard', 10],
      ]);

      const positiveAnswers = document.details
        .filter((item) => sortMap.has(item.fieldName))
        .map((item) => ({
          ...item,
          sort: sortMap.get(item.fieldName) || 0,
        }))
        .filter((item) => item.fieldValue === true);

      if (positiveAnswers.length) {
        return positiveAnswers.map((item) => ({
          message: `Yes answer on Question ${item.sort}`,
          type: 'warning' as AlertColor,
        }));
      }
    }
    return [];
  }, [document.details]);

  if (document.status === DocumentStatusEnum.DELETED) {
    return (
      <div className={styles.root}>
        <Divider />
        <DocumentReviewHeader
          titlePrev={titlePrev}
          titleNext={titleNext}
          onPrev={onPrev}
          onNext={onNext}
          title={document.documentName}
          notifications={[]}
          canEditDocumentType={false}
          document={document}
        />
        <div className={styles.content}>
          <div className={styles.purged}>The document has been purged</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Divider />

      {!isReadOnly && (
        <DocumentReviewHeader
          titlePrev={titlePrev}
          titleNext={titleNext}
          onPrev={onPrev}
          onNext={onNext}
          title={
            document.documentName +
            (document.status === DocumentStatusEnum.REJECTED
              ? ' - REJECTED'
              : '')
          }
          notifications={notifications}
          canEditDocumentType={false}
          document={document}
        />
      )}

      <div className={styles.content}>
        {!isReadOnly && (
          <div className={styles.documentActions}>
            {!locked && (
              <OutlinedButton
                size="small"
                onClick={handleReject}
                disabled={false}
              >
                Reject
              </OutlinedButton>
            )}
            {!locked && (
              <ButtonWithArrowIcon
                size="small"
                onClick={handleApprove}
                disabled={false}
              >
                Approve
              </ButtonWithArrowIcon>
            )}
            {document.status === DocumentStatusEnum.APPROVED && (
              <ButtonWithArrowIcon
                size="small"
                onClick={onDownload}
                downArrow={true}
              >
                Download
              </ButtonWithArrowIcon>
            )}
          </div>
        )}

        <div className={styles.formWrapper}>
          <DriverChecklistCardForm
            getFieldProps={getFieldProps}
            getFieldErrorProp={getFieldErrorProp}
            disabled={true}
            candidateFullName={candidateFullName}
            employeeId={employeeId}
          />
        </div>
      </div>
      <LogEvents logs={document.documentEvents} />
    </div>
  );
};
